import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { AppDataGrid } from '../../components/AppDataGrid';
import { LoaderSymbol } from '../../components/loading';
import { fmtLocalDateTime } from '../../utils/datetime';
import '../backlog/backlog.css';   // shared .dp-breadcrumb styling (this page loads no other CSS)

// ─────────────────────────────────────────────────────────────────────────
// Settings → Default Settings → Client Version Control  (SERVICE ONLY)
// ─────────────────────────────────────────────────────────────────────────
//   Ported from ALPACA's ClientVersionControl_DetailView.  Sets the purchased
//   CONCURRENT-USER LICENCE — the same encrypted row ALPACA reads
//   (dbo.ClientVersionControl.ConcurrentUsersval), so both products always
//   agree, and every change writes dbo.ConcurrentUserChangeLog exactly as
//   ALPACA's own screen does (history stays continuous across products).
//
//   This is the commercial entitlement, not a customer preference: the server
//   refuses any caller that is not the `service` account, mirroring ALPACA's
//   NavigationViewHideController.  Audited as "Client Version Control".
//
//   Layout FILLS the viewport: the licence card spans the full width (stat ·
//   number · note, then a metadata strip) and the history card grows into
//   whatever vertical space is left — no dead whitespace (§18.6).
// ─────────────────────────────────────────────────────────────────────────

interface ChangeLogRow {
  oid: string;
  oldValue: number | null;
  newValue: number | null;
  changedOn: string | null;
  changedBy: string | null;
  note: string | null;
}

interface LicenseDetail {
  concurrentUsers: number | null;
  date: string | null;
  setBy: string | null;
  note: string | null;
  lastUpdatedDate: string | null;
  exists: boolean;
  licenseSource: string;
  licenseProblem: string | null;
  changeLog: ChangeLogRow[];
}

type SourceLabel = { text: string; tone: 'ok' | 'warn' };
const SOURCE_LABEL: Record<string, SourceLabel> = {
  Alpaca:      { text: 'Licensed',           tone: 'ok' },
  NotLicensed: { text: 'No licence on file', tone: 'warn' },
  Unreadable:  { text: 'Licence unreadable', tone: 'warn' },
};

export default function ClientVersionControlPage({ onHome }: { onHome?: () => void }) {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [users, setUsers] = useState<string>('');
  const [initialUsers, setInitialUsers] = useState<string>('');
  const [note, setNote] = useState('');
  const [detail, setDetail] = useState<LicenseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  // Enforcement switch — lives HERE with the licence it governs (App Config is
  // hidden during the timezone migration, ADR-0008, and is not ours to change).
  const [enforce, setEnforce] = useState(false);
  const [initialEnforce, setInitialEnforce] = useState(false);
  // Read-only display of the ONE app-wide inactivity window (Screen Auto Lock).
  // Shown here because it governs when a licence is reclaimed, edited on its own
  // form — no `initial` twin, since this page can no longer change it.
  const [idleMin, setIdleMin] = useState(60);
  const [beatSec, setBeatSec] = useState(60);
  const [initialBeatSec, setInitialBeatSec] = useState(60);

  const load = () => {
    setLoading(true); setErrMsg(null);
    const t0 = performance.now();
    void api.get('/api/v1/app-config', { silentLoading: true } as never)
      .then((r) => {
        const c = (r.data as { data?: { concurrentLoginEnabled?: boolean; sessionIdleTimeoutMinutes?: number; sessionHeartbeatSeconds?: number } })?.data;
        const on = c?.concurrentLoginEnabled === true;
        const im = c?.sessionIdleTimeoutMinutes ?? 60;
        const hb = c?.sessionHeartbeatSeconds ?? 60;
        setEnforce(on); setInitialEnforce(on);
        setIdleMin(im);
        setBeatSec(hb); setInitialBeatSec(hb);
      })
      .catch(() => { /* non-fatal — the licence still renders */ });

    return api.get('/api/v1/client-version-control')
      .then((res) => {
        const d = (res.data as { data?: LicenseDetail })?.data ?? null;
        setDetail(d);
        const u = d?.concurrentUsers == null ? '' : String(d.concurrentUsers);
        setUsers(u); setInitialUsers(u);
        setNote('');
        setForbidden(false);
      })
      .catch((e) => {
        const status = (e as { response?: { status?: number } }).response?.status;
        if (status === 403) {
          setForbidden(true);
          setErrMsg('The concurrent-user licence can only be viewed and changed by the service account.');
        } else {
          setErrMsg((e as { message?: string }).message ?? 'Failed to load.');
        }
      })
      .finally(() => { setLoadMs(performance.now() - t0); setLoading(false); });
  };
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const parsed = Number(users.trim());
  const valid = users.trim() !== '' && Number.isFinite(parsed) && parsed > 0;

  const persist = async () => {
    if (!valid) throw new Error('Enter the licensed number of concurrent users (greater than zero).');
    await api.put('/api/v1/client-version-control', {
      concurrentUsers: Math.floor(parsed),
      note: note.trim() || null,
    });
    setInitialUsers(users);

    // No idleTimeoutMinutes — the inactivity window is Screen Auto Lock now
    // (2026-07-30) and is edited on its own form, not here.
    if (enforce !== initialEnforce || beatSec !== initialBeatSec) {
      await api.put('/api/v1/app-config/concurrent-login', {
        enabled: enforce, heartbeatSeconds: beatSec,
      });
      setInitialEnforce(enforce); setInitialBeatSec(beatSec);
    }
  };

  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Client Version Control',
      successMessage: 'Licence saved successfully.',
      save: persist,
      reload: load,
      onError: (e: unknown) => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        ?? (e as { message?: string }).message ?? 'Save failed.'),
    }).finally(() => setSaving(false));
  };
  const handleCancel = () => gate.cancel(() => void load());

  const rows = useMemo(() => detail?.changeLog ?? [], [detail]);
  const src: SourceLabel =
    SOURCE_LABEL[detail?.licenseSource ?? 'Alpaca'] ?? { text: 'Licensed', tone: 'ok' };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 13 }}>
        <LoaderSymbol size={14} /> Loading licence…
      </div>
    );
  }

  // ── Shared BO-standard styles ────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: 'var(--ai-card, #fff)', border: '1px solid var(--ai-line)', borderRadius: 10,
    boxShadow: '0 1px 3px rgba(20,24,40,.05)', padding: '16px 18px', marginBottom: 14,
  };
  const secTitle: React.CSSProperties = {
    fontSize: 13, fontWeight: 700, color: 'var(--ai-blue)', marginBottom: 12,
    display: 'flex', alignItems: 'center', gap: 6,
  };
  const lbl: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)', display: 'block', marginBottom: 5,
  };
  // Standard control size (§18.6 / rule 12 — the Items reference form): 26px tall
  // via the shared tokens, not the old oversized 7px padding (owner request 2026-10-15).
  const inp: React.CSSProperties = {
    padding: 'var(--ai-control-pad-y, 4px) 10px', minHeight: 'var(--ai-control-h, 26px)',
    fontSize: 13, border: '1px solid var(--ai-line)', borderRadius: 0,
    width: '100%', boxSizing: 'border-box', fontFamily: 'var(--ai-font)',
  };
  // Side-label standard (rule 12): label BESIDE the field, left-aligned, on one line
  // (owner request 2026-10-15 "label and input horizontal").
  const lblSide: React.CSSProperties = {
    flex: 'none', fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)', whiteSpace: 'nowrap',
  };
  const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10 };
  const meta: React.CSSProperties = { fontSize: 12.5, color: 'var(--ai-ink)' };
  const metaLbl: React.CSSProperties = { fontSize: 11.5, color: 'var(--ai-muted)', marginBottom: 2 };

  return (
    // Proven full-height geometry (same as AuditTrailPage/.bl-page): the PAGE
    // does not scroll — the history grid scrolls internally — so `flex: 1` on
    // the history card genuinely stretches to the bottom of the viewport.
    <div style={{
      height: '100%', overflow: 'hidden', boxSizing: 'border-box',
      display: 'flex', flexDirection: 'column',
      padding: '14px 20px', background: 'var(--ai-bg)', fontFamily: 'var(--ai-font)',
    }}>
      <>
        {/* Navigator breadcrumb (owner request 2026-10-14) — this page had none.
            Home › Settings › Default Settings › Client Version Control. */}
        <div className="dp-breadcrumb" style={{ marginBottom: 8 }}>
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>Default Settings</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Client Version Control</span>
        </div>
        <FormTitle pageId="clientversion" auditFormName="Client Version Control" loadMs={loadMs} loading={loading} />

        {errMsg && (
          <div style={{
            background: 'var(--ai-pill-alert-bg)', border: '1px solid var(--ai-pill-alert-fg)',
            color: 'var(--ai-pill-alert-fg)', padding: '10px 14px', borderRadius: 6,
            fontSize: 12, marginBottom: 14,
          }}>
            <i className="ti ti-alert-triangle" />&nbsp;{errMsg}
          </div>
        )}

        {!forbidden && (
          <>
            <div style={{ margin: '0 0 12px', flex: '0 0 auto' }}>
              <AppToolbar
                left={(
                  <EditGateActions
                    gate={gate}
                    saving={saving}
                    disabled={loading || (gate.editing && !valid)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                  />
                )}
                onRefresh={() => void load()}
                refreshing={loading}
                refreshDisabled={gate.editing}
              />
            </div>

            {/* ── Licence card — spans the full width ───────────────────── */}
            <div style={{ ...card, flex: '0 0 auto' }}>
              <div style={secTitle}><i className="ti ti-license" /> Licence</div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(190px, 230px) minmax(250px, 300px) minmax(220px, 1fr)',
                gap: 20, alignItems: 'center',
              }}>
                {/* Entitlement, read large */}
                <div style={{
                  padding: '14px 18px', borderRadius: 0, boxSizing: 'border-box',
                  border: '1px solid var(--ai-line)', background: '#fafbfc',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}>
                  <div style={{ fontSize: 11, letterSpacing: .3, textTransform: 'uppercase', color: 'var(--ai-muted)', marginBottom: 6 }}>
                    Concurrent users
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 34, fontWeight: 700, lineHeight: 1, color: 'var(--ai-ink)' }}>
                      {detail?.concurrentUsers ?? '—'}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--ai-muted)' }}>users</span>
                  </div>
                  <div style={{
                    marginTop: 10, alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
                    color: src.tone === 'ok' ? 'var(--ai-green, #1e7a3c)' : 'var(--ai-pill-alert-fg)',
                    background: src.tone === 'ok' ? 'var(--ai-green-tint, #eaf6ec)' : 'var(--ai-pill-alert-bg)',
                  }}>
                    <i className={`ti ${src.tone === 'ok' ? 'ti-circle-check' : 'ti-alert-triangle'}`} />
                    {src.text}
                  </div>
                </div>

                {/* Concurrent users — label beside the input (horizontal) */}
                <div style={fieldRow}>
                  <label style={lblSide} htmlFor="cvc-users">
                    Concurrent users <span style={{ color: '#c0392b' }}>*</span>
                  </label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      id="cvc-users" type="number" min={1} step={1}
                      style={{ ...inp, background: !ro && !valid ? '#fffbe7' : (ro ? '#f6f7f9' : undefined) }}
                      value={users} disabled={ro} placeholder="e.g. 5"
                      onChange={(e) => setUsers(e.target.value)}
                    />
                    {!ro && !valid && (
                      <div style={{ marginTop: 4, fontSize: 11, color: '#c0392b' }}>
                        Required — must be greater than zero.
                      </div>
                    )}
                    {!ro && valid && users !== initialUsers && (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ai-orange-hover)' }}>
                        {initialUsers || '—'} → {Math.floor(parsed)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Note — label beside the input (horizontal); input fills the rest */}
                <div style={fieldRow}>
                  <label style={lblSide} htmlFor="cvc-note">Note</label>
                  <input
                    id="cvc-note" type="text" maxLength={100}
                    style={{ ...inp, flex: 1, minWidth: 0, background: ro ? '#f6f7f9' : undefined }}
                    value={note} disabled={ro}
                    placeholder="Reason for this change — saved with the history entry"
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              </div>

              {/* Metadata spread evenly across the full card width */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: '12px 20px', marginTop: 18, paddingTop: 14,
                borderTop: '1px solid var(--ai-line-soft, var(--ai-line))',
              }}>
                <div><div style={metaLbl}>Set by</div><div style={meta}>{detail?.setBy || '—'}</div></div>
                <div><div style={metaLbl}>Set on</div><div style={meta}>{detail?.date ? fmtLocalDateTime(detail.date) : '—'}</div></div>
                <div><div style={metaLbl}>Last updated</div><div style={meta}>{detail?.lastUpdatedDate ? fmtLocalDateTime(detail.lastUpdatedDate) : '—'}</div></div>
                <div><div style={metaLbl}>Last note</div><div style={meta}>{detail?.note || '—'}</div></div>
              </div>

              {detail?.licenseProblem && (
                <div style={{ marginTop: 14, fontSize: 11.5, color: 'var(--ai-pill-alert-fg)' }}>
                  <i className="ti ti-alert-triangle" />&nbsp;{detail.licenseProblem}
                </div>
              )}
            </div>

            {/* ── Enforcement — the switch that governs this licence ────── */}
            <div style={{ ...card, flex: '0 0 auto' }}>
              <div style={secTitle}><i className="ti ti-shield-check" /> Enforcement</div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: ro ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={enforce} disabled={ro} onChange={(e) => setEnforce(e.target.checked)} />
                <span style={{ fontWeight: 600, color: 'var(--ai-ink)' }}>
                  Enforce this limit at sign-in
                </span>
              </label>
              <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', marginTop: 6, maxWidth: 820 }}>
                When on, only {detail?.concurrentUsers ?? 'the licensed number of'} different users may be
                signed in at once. One user may sign in from as many PCs or browsers as they like — all of
                their sessions share that user's single licence. When off, sign-in is unrestricted.
              </div>
              {enforce && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                  gap: '12px 20px', marginTop: 14, paddingTop: 12,
                  borderTop: '1px solid var(--ai-line-soft, var(--ai-line))',
                }}>
                  {/* Read-only since 2026-07-30. This used to be its own editable
                      number (default 5 min), which made it the REAL application
                      timeout: it is checked on every request, so any gap in the
                      heartbeat longer than it — a closed lid, a sleeping machine,
                      a dropped VPN — signed the user out well before the
                      configured Screen Auto Lock. One window now, edited there. */}
                  <div>
                    <label style={lbl}>Inactivity timeout</label>
                    <div style={{ ...inp, maxWidth: 140, background: '#f6f7f9', color: 'var(--ai-ink)' }}>
                      {idleMin} minutes
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 4 }}>
                      Set in <strong>Default Settings → Screen Auto Lock</strong> — one timeout for the
                      whole application. A browser closed normally releases its licence straight away.
                      If a machine crashed or lost power, don't wait for this — free the licence with{' '}
                      <strong>Release</strong> on <strong>Data Center → User Activity</strong>, available
                      about three minutes after that machine stops responding.
                    </div>
                  </div>
                  <div>
                    <label style={lbl} htmlFor="cvc-beat">Heartbeat (seconds)</label>
                    <input id="cvc-beat" type="number" min={15} max={600} disabled={ro}
                      style={{ ...inp, maxWidth: 140, background: ro ? '#f6f7f9' : undefined }}
                      value={beatSec}
                      onChange={(e) => setBeatSec(Math.max(15, Math.min(600, Number(e.target.value) || 60)))} />
                    <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 4 }}>
                      How often each signed-in browser checks in.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Change history — grows into the remaining height ───────── */}
            <div style={{ ...card, flex: 1, minHeight: 0, marginBottom: 10, display: 'flex', flexDirection: 'column' }}>
              <div style={{ ...secTitle, justifyContent: 'space-between', flex: '0 0 auto' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="ti ti-history" /> Change history
                </span>
                <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--ai-muted)' }}>
                  {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <AppDataGrid
                  rows={rows}
                  getRowId={(r: ChangeLogRow) => r.oid}
                  defaultPageSize={10}
                  emptyText="No licence changes recorded yet — the current value is the original entitlement."
                  columns={[
                    { field: 'changedOn', header: 'Changed on', type: 'datetime', width: 180 },
                    { field: 'oldValue',  header: 'From', align: 'right', width: 90,
                      render: (r: ChangeLogRow) => <>{r.oldValue ?? '—'}</> },
                    { field: 'newValue',  header: 'To', align: 'right', width: 90,
                      render: (r: ChangeLogRow) => <>{r.newValue ?? '—'}</> },
                    { field: 'delta', header: 'Change', align: 'right', width: 110, sortable: false, filterable: false,
                      value: (r: ChangeLogRow) => (r.newValue ?? 0) - (r.oldValue ?? 0),
                      render: (r: ChangeLogRow) => {
                        if (r.oldValue == null || r.newValue == null) return <>—</>;
                        const d = r.newValue - r.oldValue;
                        if (d === 0) return <>—</>;
                        return (
                          <span style={{ color: d > 0 ? 'var(--ai-green, #1e7a3c)' : '#c0392b', fontWeight: 600 }}>
                            {d > 0 ? `+${d}` : d}
                          </span>
                        );
                      } },
                    { field: 'changedBy', header: 'Changed by', width: 180 },
                    { field: 'note', header: 'Note', minWidth: 240 },
                  ]}
                />
              </div>
            </div>

            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--ai-muted)', flex: '0 0 auto' }}>
              Only the <code>service</code> account can view or change the licence and its enforcement.
            </div>
          </>
        )}
      </>
    </div>
  );
}
